import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import Orders from "./Orders";
import WishlistPage from "./WishlistPage";
import "./Profile.css";

export default function Profile() {
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 'details', 'orders', 'wishlist'
  const [isSaved, setIsSaved] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", user.id)
        .single();

      if (data && !error) {
        // Handle name splitting if stored as full_name
        let fn = "";
        let ln = "";
        if (data.full_name) {
          const parts = data.full_name.split(" ");
          fn = parts[0];
          ln = parts.slice(1).join(" ");
        }

        setFormData({
          first_name: data.first_name || fn,
          last_name: data.last_name || ln,
          phone: data.phone || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          pincode: data.pincode || "",
        });
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const fullName = `${formData.first_name} ${formData.last_name}`.trim();
      
      const { error } = await supabase
        .from("users")
        .update({
          full_name: fullName,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        })
        .eq("id", user.id);

      if (error) throw error;
      
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 7000);
      setMessage({ text: "Profile details saved successfully!", type: "success" });
    } catch (err) {
      setMessage({ text: "Failed to save profile details. Please check database columns.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profilePage">
        <div className="profileWrap">Loading your profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profilePage" style={{ display: "flex", justifyContent: "center", minHeight: "50vh" }}>
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center", 
          textAlign: "center", 
          padding: "80px 20px", 
          gap: "16px",
          width: "100%"
        }}>
          <h2 style={{ margin: 0, fontSize: "32px", color: "#7a0f1b", fontFamily: "'Gallient', serif" }}>Please Log In</h2>
          <p style={{ margin: 0, fontSize: "18px", color: "#444" }}>You must be authenticated to view your profile.</p>
          <Link to="/login" className="profileBtn" style={{ marginTop: "12px", padding: "12px 28px", borderRadius: "30px", textDecoration: "none" }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="profilePage">
      <div className="profileWrap">
        
        <div className="profileSidebar">
          <div className="profileAvatar">
            {formData.first_name.charAt(0) || user.email.charAt(0).toUpperCase()}
          </div>
          <h3 className="profileNameTitle">{user.user_metadata?.full_name || user.email}</h3>
          <p className="profileEmailTitle">{user.email}</p>
          
          <nav className="profileNav">
            <button onClick={() => setActiveTab("details")} className={`profileNavLink ${activeTab === 'details' ? 'active' : ''}`}>Personal Details</button>
            <button onClick={() => setActiveTab("orders")} className={`profileNavLink ${activeTab === 'orders' ? 'active' : ''}`}>My Orders</button>
            <button onClick={() => setActiveTab("wishlist")} className={`profileNavLink ${activeTab === 'wishlist' ? 'active' : ''}`}>Wishlist</button>
            <button onClick={signOut} className="profileNavLink logoutBtn">Sign Out</button>
          </nav>
        </div>

        <div className="profileContent">
          {activeTab === "details" && (
            <>
              <h2 className="profileSectionTitle">Personal Details</h2>
              <p className="profileSectionSub">Update your contact and primary delivery information.</p>
              
              {message.text && (
                <div className={`profileAlert ${message.type}`}>
                  {message.text}
                </div>
              )}

          <form onSubmit={handleSave} className="profileForm">
            <div className="formRow two-col">
              <div className="inputGroup">
                <label>First Name</label>
                <input 
                  type="text" 
                  name="first_name" 
                  value={formData.first_name} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="inputGroup">
                <label>Last Name</label>
                <input 
                  type="text" 
                  name="last_name" 
                  value={formData.last_name} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>

            <div className="formRow two-col">
              <div className="inputGroup">
                <label>Email Address</label>
                <input type="email" value={user.email} disabled className="disabledInput" />
              </div>
              <div className="inputGroup">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>

            <h3 className="profileSubHeading">Default Shipping Address</h3>
            
            <div className="formRow">
              <div className="inputGroup">
                <label>Street Address</label>
                <input 
                  type="text" 
                  name="address" 
                  value={formData.address} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>

            <div className="formRow three-col">
              <div className="inputGroup">
                <label>City</label>
                <input 
                  type="text" 
                  name="city" 
                  value={formData.city} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="inputGroup">
                <label>State</label>
                <input 
                  type="text" 
                  name="state" 
                  value={formData.state} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="inputGroup">
                <label>Pincode</label>
                <input 
                  type="text" 
                  name="pincode" 
                  value={formData.pincode} 
                  onChange={handleChange} 
                  required 
                  maxLength="6"
                />
              </div>
            </div>

            <div className="profileFormActions">
              <button type="submit" className="profileBtn primary" disabled={saving || isSaved}>
                {saving ? "Saving..." : isSaved ? "Saved" : "Save Details"}
              </button>
            </div>
          </form>
          </>
          )}

          {activeTab === "orders" && (
            <div className="profileTabWrapper">
              <Orders embedded={true} />
            </div>
          )}

          {activeTab === "wishlist" && (
            <div className="profileTabWrapper" style={{ paddingTop: "0px" }}>
              <WishlistPage embedded={true} />
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
